import { useEffect, useMemo, useRef } from "react";

import { buildFleetGeometry } from "../lib/geometry";
import { isHighlighted } from "../lib/selection";
import type { Selection } from "../lib/selection";
import type { TopologyPayload } from "../lib/types/topology";
import type { ViewMode } from "../lib/viewMode";

import Fuselage      from "./Fuselage";
import PortRails     from "./PortRails";
import SeatGrid      from "./SeatGrid";
import TopTierRails  from "./TopTierRails";


interface FleetSVGProps {
  topology: TopologyPayload;
  selection?: Selection;
  onSelect?: (s: Selection | undefined) => void;
  /** Double-click select: same selection, plus the LOPA jumps to the
   *  highlighted seat block. Single clicks only re-highlight in place. */
  onActivate?: (s: Selection) => void;
  /** Bumped by FleetPage on every double-click — triggers the jump. */
  jumpToken?: number;
  viewMode: ViewMode;
  /** Render scale (mobile pinch-zoom) — viewBox is unchanged, so the SVG
   *  content scales losslessly with the width/height. */
  scale?: number;
  /** Desktop: scale the whole drawing to the pane width so there is never
   *  horizontal scrolling — only vertical. */
  fitWidth?: boolean;
  /** Availability view: per-seat scores for the selected demo trip. */
  avail?: Record<string, import("../lib/types/demo").TripSeat>;
  /** Incident selection: explicit bright-seat set (overrides chain logic). */
  brightSeats?: Set<string> | null;
}


export default function FleetSVG({
  topology, selection, onSelect, onActivate, jumpToken = 0,
  viewMode, scale = 1, fitWidth = false, avail, brightSeats,
}: FleetSVGProps) {
  const geometry = useMemo(
    () => buildFleetGeometry(topology, viewMode),
    [topology, viewMode],
  );
  const { viewBoxX, totalWidth, totalHeight } = geometry;
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    // Jumps are opt-in: a DOUBLE-click (or an availability-panel incident
    // click) bumps jumpToken and the LOPA scrolls to the highlighted seat
    // block with adaptive placement (taller than the pane → top-aligned;
    // smaller — down to a single seat — centred). Single clicks only update
    // highlights in place, so inspecting the head-end tree never yanks the
    // viewport away.
    if (jumpToken === 0 || !selection) return;
    const svg = svgRef.current;
    if (!svg) return;

    const ys: number[] = [];
    for (const [sid, s] of Object.entries(topology.seats)) {
      // Incident selections highlight via the explicit seat set; everything
      // else goes through the topology-driven chain highlighting.
      const bright = selection.kind === "incident"
        ? (brightSeats?.has(sid) ?? false)
        : isHighlighted(selection, { kind: "seat", seatId: sid }, topology,
            viewMode === "mcu" ? "mcu" : "data");
      if (bright) {
        const y = geometry.rowYs.get(s.row);
        if (y !== undefined) ys.push(y);
      }
    }
    if (ys.length === 0) return;
    const cellH = topology.layout.canvas.cell_h;
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys) + cellH;

    // Nearest scrollable ancestor (desktop pane or mobile pinch pane).
    let sc: HTMLElement | null = svg.parentElement;
    while (sc && sc.scrollHeight <= sc.clientHeight + 1) sc = sc.parentElement;
    if (!sc) return;

    // Render scale + the SVG's offset inside the scroll content.
    const renderScale = svg.getBoundingClientRect().width / totalWidth;
    const contentTop =
      svg.getBoundingClientRect().top - sc.getBoundingClientRect().top + sc.scrollTop;
    const blockTopPx = contentTop + minY * renderScale;
    const blockHPx = (maxY - minY) * renderScale;
    const paneH = sc.clientHeight;

    const target = blockHPx >= paneH - 24
      ? blockTopPx - 12                          // tall block → align top
      : blockTopPx - (paneH - blockHPx) / 2;     // short block → centre
    sc.scrollTo({ top: Math.max(0, target), behavior: "smooth" });
    // Deliberately keyed on jumpToken alone: the token only changes on
    // double-click / incident click, and that render already carries the
    // final selection.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jumpToken]);

  // Background click intentionally does NOT clear selection — too easy to misfire.
  return (
    <svg
      ref={svgRef}
      viewBox={`${viewBoxX} 0 ${totalWidth} ${totalHeight}`}
      {...(fitWidth
        ? {}
        : { width: totalWidth * scale, height: totalHeight * scale })}
      style={{
        // Double-click is a jump gesture — suppress the browser's
        // text-selection flash on labels.
        userSelect: "none",
        ...(fitWidth
          ? { width: "100%", height: "auto", aspectRatio: `${totalWidth} / ${totalHeight}` }
          : {}),
      }}
      fontFamily="Inter, system-ui, sans-serif"
      className="block"
      role="img"
      aria-label={`${topology.fleet_label} layout`}
    >
      <defs>
        {/* Soft elevation for head-end hardware cards (not port sockets). */}
        <filter id="lopaCardShadow" x="-20%" y="-20%" width="140%" height="160%">
          <feDropShadow dx="0" dy="1.5" stdDeviation="1.6"
                        floodColor="#0F172A" floodOpacity="0.22" />
        </filter>
      </defs>
      <rect
        x={viewBoxX} y={0} width={totalWidth} height={totalHeight}
        fill="#FAFBFC"
      />
      {/* Fuselage first (pure backdrop) so head-end boxes stay clickable
          where the nose overlaps them (76L etc.). */}
      <Fuselage   geometry={geometry} />
      <SeatGrid   geometry={geometry} topology={topology}
                  selection={selection} onSelect={onSelect}
                  onActivate={onActivate} viewMode={viewMode}
                  avail={avail} brightSeats={brightSeats} />
      <TopTierRails geometry={geometry} topology={topology}
                    selection={selection} onSelect={onSelect}
                    onActivate={onActivate} viewMode={viewMode} />
      <PortRails    geometry={geometry} topology={topology}
                    selection={selection} onSelect={onSelect}
                    onActivate={onActivate} viewMode={viewMode} />
    </svg>
  );
}
